import React, { useEffect, useRef, useState } from "react";
import { CornerDownRight, Send, Loader2, AlertTriangle } from "lucide-react";
import { parseAnswer, streamFollowUp, KIND_LABEL, plainName } from "../../lib/searchApi";
import { cn } from "../../lib/utils";

const MAX = 8;
const LEVEL = { HIGH: "text-critical", MEDIUM: "text-warning", LOW: "text-info" };

function useFollowUps(id, initial, stream) {
  const [turns, setTurns] = useState(initial || []);
  const [live, setLive] = useState(null);
  const ctl = useRef(null);
  useEffect(() => () => ctl.current?.abort(), []);
  const ask = async (q) => {
    const c = new AbortController();
    ctl.current = c;
    setLive({ q, text: "", entities: [], not_found: [], error: null });
    try {
      await stream(id, q, {
        signal: c.signal,
        onMeta: m => setLive(p => ({ ...p, entities: m.entities || [], not_found: m.not_found || [] })),
        onToken: t => setLive(p => ({ ...p, text: p.text + t })),
        onDone: d => { setTurns(ts => [...ts, d.turn]); setLive(null); },
      });
    } catch (e) {
      if (e.name !== "AbortError") setLive(p => ({ ...p, error: e.message }));
    }
  };
  return { turns, live, ask, busy: !!live && !live.error };
}

function altLabel(o, list) {
  const n = plainName(o.name);
  const dup = list.filter(x => plainName(x.name) === n).length > 1;
  return [o.code, n, dup && o.detail, o.active === false && "no cleaned activity"].filter(Boolean).join(" · ");
}

function Alternatives({ t, i, onAsk, disabled, p }) {
  const alts = (t.entities || []).filter(e => e.matched_name && e.others?.length);
  if (!alts.length) return null;
  const swap = (e, code) => t.q.replace(new RegExp(e.matched_name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i"), code);
  return (
    <div className="pl-5 space-y-1" data-testid={`${p}-alts-${i}`}>
      {alts.map(e => (
        <div key={e.code} className="flex flex-wrap items-center gap-1.5 text-[10.5px] text-muted">
          <span>“{e.matched_name}” also matches:</span>
          {e.others.map(o => (
            <button key={o.code} type="button" disabled={disabled} data-testid={`${p}-alt-${i}-${o.code}`} onClick={() => onAsk(swap(e, o.code))}
                    title={`Ask again with ${o.code}`}
                    className="px-1.5 py-0.5 border border-border rounded-sm hover:border-primary hover:text-primary transition-colors disabled:opacity-50">
              {altLabel(o, [e, ...e.others])}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

function Turn({ t, i, streaming, onAsk, disabled, p }) {
  const a = parseAnswer(t.text);
  return (
    <div data-testid={`${p}-turn-${i}`} className="space-y-2 animate-fade-in">
      <div className="flex items-start gap-2 text-[13px] font-medium"><CornerDownRight size={13} className="text-primary mt-1 shrink-0" />{t.q}</div>
      {(t.entities?.length > 0 || t.not_found?.length > 0) && (
        <div className="pl-5 flex flex-wrap gap-1.5 text-[10.5px]">
          {t.entities?.map(e => (
            <span key={e.code} data-testid={`${p}-entity-${i}-${e.code}`} className="px-1.5 py-0.5 border border-primary/30 text-primary rounded-sm">
              {KIND_LABEL[e.kind]} {e.kind === "engine" ? "" : e.code}{e.name ? `${e.kind === "engine" ? "" : " · "}${plainName(e.name)}` : ""}{e.matched_name ? ` (from “${e.matched_name}”)` : ""}
            </span>
          ))}
          {t.not_found?.map(c => <span key={c} className="px-1.5 py-0.5 border border-border text-muted rounded-sm">{c} not found</span>)}
        </div>
      )}
      {!streaming && <Alternatives t={t} i={i} onAsk={onAsk} disabled={disabled} p={p} />}
      <div data-testid={`${p}-answer-${i}`} className="pl-5 border-l-2 border-border ml-1.5 space-y-1.5 text-[13px] leading-relaxed">
        {a.lines.length ? a.lines.map((x, k) => <p key={`l${k}`}>{x}</p>) : !t.error && <p className="text-muted inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Thinking…</p>}
        {a.insights.map((x, k) => <p key={k}>{x}</p>)}
        {a.recs.map((r, k) => (
          <p key={k}><span className={cn("text-[10px] font-semibold uppercase tracking-wider mr-1.5", LEVEL[r.level] || "text-muted")}>{r.level || "Next"}</span>{r.text}</p>
        ))}
        {streaming && a.summary && <span className="inline-block w-1.5 h-3.5 bg-primary/70 animate-pulse" aria-hidden />}
        {t.error && <p data-testid={`${p}-error`} className="text-critical inline-flex items-center gap-1.5"><AlertTriangle size={12} /> {t.error}</p>}
      </div>
    </div>
  );
}

function Suggestions({ items, onAsk, p }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${p}-suggestions`}>
      {items.map((q, i) => (
        <button key={q} type="button" data-testid={`${p}-suggestion-${i}`} onClick={() => onAsk(q)}
                className="text-[11.5px] px-2 py-1 border border-border rounded-sm hover:border-primary hover:text-primary transition-colors">{q}</button>
      ))}
    </div>
  );
}

// Threaded follow-ups under a saved AI answer (AI Search by default; the Morning Brief explainer passes its own stream).
export function FollowUps({ id, initial, stream = streamFollowUp, prefix = "ai-followup", title = "Follow-up questions", suggestions = [], seed, placeholder }) {
  const { turns, live, ask, busy } = useFollowUps(id, initial, stream);
  const [q, setQ] = useState("");
  const full = turns.length >= MAX;
  const p = prefix;
  useEffect(() => { if (seed?.q && !busy && !full) ask(seed.q); }, [seed]);
  const send = (e) => { e.preventDefault(); if (q.trim().length >= 2 && !busy && !full) { ask(q.trim()); setQ(""); } };
  return (
    <section data-testid={`${p}s`} className="space-y-4 border-t border-border pt-4">
      <div className="kpi-label text-[10px]">{title}</div>
      {turns.map((t, i) => <Turn key={`${i}-${t.created_at}`} t={t} i={i} onAsk={ask} disabled={busy || full} p={p} />)}
      {live && <Turn t={live} i={turns.length} streaming={busy} onAsk={ask} disabled p={p} />}
      {!turns.length && !live && suggestions.length > 0 && <Suggestions items={suggestions} onAsk={ask} p={p} />}
      <form onSubmit={send} className="flex items-center gap-2">
        <input data-testid={`${p}-input`} value={q} onChange={e => setQ(e.target.value)} maxLength={300} disabled={busy || full}
               placeholder={full ? `Up to ${MAX} follow-ups — start again to keep going` : placeholder || "Ask a follow-up, e.g. “compare with Davao Lanang” or “… with BR-005”"}
               className="flex-1 min-w-0 h-9 px-3 bg-surface-2 border border-border rounded-sm text-[13px] outline-none focus:border-primary/60 placeholder:text-subtle disabled:opacity-60" />
        <button type="submit" data-testid={`${p}-send`} disabled={busy || full || q.trim().length < 2} aria-label="Send follow-up"
                className="h-9 w-9 shrink-0 inline-flex items-center justify-center bg-primary text-primary-fg rounded-sm disabled:opacity-40 hover:opacity-90 active:scale-[0.96] transition-[opacity,transform]">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
    </section>
  );
}
